import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Menu, X, Globe, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { assets, socials, contactInfo } from '../mock';

const Navbar = () => {
  const { t, lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 350);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goPage = (path) => {
    setOpen(false);
    navigate(path);
  };

  // ordered nav: sections + pages
  const items = [
    { type: 's', id: 'home', label: t.nav.home },
    { type: 's', id: 'about', label: t.nav.about },
    { type: 's', id: 'services', label: t.nav.services },
    { type: 's', id: 'videos', label: t.nav.videos },
    { type: 'p', path: '/eventos-culturales', label: t.nav.cultural },
    { type: 'p', path: '/laura-dickinson', label: t.nav.laura },
    { type: 's', id: 'packages', label: t.nav.packages },
    { type: 's', id: 'contact', label: t.nav.contact },
  ];

  const handle = (it) => (it.type === 's' ? goSection(it.id) : goPage(it.path));

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#14120d]/95 backdrop-blur-md border-b border-[#e6b83f]/15' : 'bg-[#14120d]/40 backdrop-blur-sm'
      }`}
    >
      {/* Top utility bar */}
      <div className={`border-b border-[#e6b83f]/10 transition-all duration-500 overflow-hidden ${scrolled ? 'max-h-0 opacity-0' : 'max-h-12 opacity-100'}`}>
        <div className="max-w-7xl mx-auto px-5 h-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {socials.map((s) => {
              const Icon = Icons[s.icon] || Icons.Music;
              return (
                <a key={s.name} href={s.url} target="_blank" rel="noreferrer" aria-label={s.name}
                  className="text-[#cfc8b8] hover:text-[#f5deA0] transition-colors">
                  <Icon size={16} strokeWidth={1.6} />
                </a>
              );
            })}
          </div>
          <a href={`tel:${contactInfo.phoneRaw}`} className="hidden sm:flex items-center gap-2 text-[#e0d9c8] hover:text-[#f5deA0] transition-colors font-sans-el text-[13px] tracking-wide">
            <Phone size={14} /> {contactInfo.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className={`max-w-7xl mx-auto px-5 flex items-center justify-between transition-all duration-500 ${scrolled ? 'py-3' : 'py-3'}`}>
        <button onClick={() => goSection('home')} className="flex items-center gap-3 shrink-0" aria-label="LAJO">
          <img src={assets.logo} alt="LAJO" className="h-11 w-auto object-contain" />
        </button>

        <nav className="hidden xl:flex items-center gap-6">
          {items.map((it) => (
            <button
              key={it.label}
              onClick={() => handle(it)}
              className="nav-link font-sans-el text-[12px] tracking-[0.16em] uppercase text-[#e8e2d4] hover:text-[#f5deA0] transition-colors whitespace-nowrap"
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="flex items-center gap-1.5 text-[12px] tracking-[0.15em] uppercase text-[#e8e2d4] hover:text-[#f5deA0] transition-colors font-sans-el"
            aria-label="Toggle language"
          >
            <Globe size={15} strokeWidth={1.5} />
            {lang === 'es' ? 'EN' : 'ES'}
          </button>
          <button
            onClick={() => goSection('contact')}
            className="hidden sm:inline-flex btn-gold px-5 py-2.5 text-[12px] tracking-[0.18em] uppercase font-medium rounded-sm"
          >
            {t.nav.cta}
          </button>
          <button className="xl:hidden text-[#f5deA0]" onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`xl:hidden overflow-hidden transition-all duration-500 ${open ? 'max-h-[640px]' : 'max-h-0'}`}>
        <nav className="bg-[#16130d]/98 backdrop-blur-md border-t border-[#e6b83f]/15 px-6 py-6 flex flex-col gap-5">
          {items.map((it) => (
            <button
              key={it.label}
              onClick={() => handle(it)}
              className="text-left font-sans-el text-sm tracking-[0.18em] uppercase text-[#e8e2d4] hover:text-[#f5deA0]"
            >
              {it.label}
            </button>
          ))}
          <div className="flex items-center gap-4 pt-2">
            {socials.map((s) => {
              const Icon = Icons[s.icon] || Icons.Music;
              return (
                <a key={s.name} href={s.url} target="_blank" rel="noreferrer" aria-label={s.name} className="text-[#cfc8b8] hover:text-[#f5deA0]">
                  <Icon size={20} />
                </a>
              );
            })}
          </div>
          <button onClick={() => goSection('contact')} className="btn-gold px-5 py-3 text-xs tracking-[0.18em] uppercase rounded-sm mt-1">
            {t.nav.cta}
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
